import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import moment from "moment";
import FieldModal from "../../components/modals/fieldModal";
import InputField from "../../components/inputs/inputField";
import ViewsTable from "../../components/tables/ViewsTable";
import LoadingWizard from "../../components/spinner/wizard";
import XLSX from "xlsx";
import { saveAs } from "file-saver";

class FormSubmissions extends Component {
	constructor(props) {
		super(props);

		// Process initial column data
		const { columns, columnTypes, toggledColumns, normalizedColumns } = this.processBaseColumns();

		// Process form field data
		const { toggledFieldLabels, normalizedFieldLabels } = this.processFormFields(
			props.form,
			toggledColumns,
			normalizedColumns
		);

		// Process custom fields
		this.processCustomFields(
			props.contactCustomFields || [],
			columns,
			columnTypes,
			toggledColumns,
			normalizedColumns
		);

		this.state = {
			loading: false,
			columns,
			columnTypes,
			normalizedColumns,
			toggledColumns,
			normalizedFieldLabels,
			toggledFieldLabels,
			checkedSubmission: {},
			submissionDetailsModalTrigger: false,
			selectedColumnSort: null,
			sortDesc: false,
			searchTerm: "",
			searching: false,
			processedSubmissions: null,
			filteredSubmissions: null,
		};

		this.searchTimeout = null;
	}

	processBaseColumns = () => {
		const columns = {
			email: "Email",
			firstName: "First Name",
			lastName: "Last Name",
			submittedAt: "Submitted",
			errors: "Errors"
		};

		const columnTypes = {
			email: "text",
			firstName: "text",
			lastName: "text",
			submittedAt: "timestamp",
			errors: "text"
		};

		const toggledColumns = {};
		const normalizedColumns = Object.keys(columns).map((cfc) => {
			toggledColumns[cfc] = true;
			return {
				label: columns[cfc],
				key: cfc,
				value: cfc,
				type: columnTypes[cfc],
				sortAsc: false,
				sortDesc: false,
			};
		});

		return { columns, columnTypes, toggledColumns, normalizedColumns };
	}

	processFormFields = (form, toggledColumns, normalizedColumns) => {
		const toggledFieldLabels = { ...toggledColumns };
		const normalizedFieldLabels = [...normalizedColumns];

		form.pages.forEach((page) => {
			page.sections.forEach((section) => {
				section.fields.forEach((field) => {
					toggledFieldLabels[field.id] = true;
					normalizedFieldLabels.push({
						key: field.id,
						label: field.label,
						type: field.type,
						value: field.id,
						sortAsc: false,
						sortDesc: false,
					});

					if (field.type === "nestedSections" || field.type === "groupSelection") {
						field.nestedSections.forEach((nestedSection) => {
							nestedSection.questions.forEach((question) => {
								toggledFieldLabels[question.id] = true;
								normalizedFieldLabels.push({
									key: question.id,
									label: question.label,
									type: question.type,
									value: question.id,
									sortAsc: false,
									sortDesc: false,
								});
							});
						});
					}
				});
			});
		});

		return { toggledFieldLabels, normalizedFieldLabels };
	}

	processCustomFields = (contactCustomFields, columns, columnTypes, toggledColumns, normalizedColumns) => {
		(contactCustomFields || []).forEach((cfcategory) => {
			(cfcategory.fields || []).forEach((field) => {
				columns[field._id] = field.fieldName;
				columnTypes[field._id] = field.fieldType;
				toggledColumns[field._id] = true;

				normalizedColumns.push({
					label: field.fieldName,
					key: field._id,
					value: field._id,
					type: field.fieldType,
					sortAsc: false,
					sortDesc: false,
				});
			});
		});
	}

	componentDidMount() {
		this.processSubmissionsData();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.editedForm.submissions !== this.props.editedForm.submissions) {
			this.processSubmissionsData();
		}
	}

	normalizeSubmissions = (submissions) => {
		if (!submissions?.length) return;
		const lastSub = submissions[submissions.length - 1];
		let masterFields = {};
		try {
			masterFields = JSON.parse(lastSub.formData && lastSub.formData.extraFields || "{}");
		} catch (e) {
			masterFields = {};
		}
		const masterKeys = Object.keys(masterFields);
		submissions.forEach((sub) => {
			let currentFields = {};
			try {
				currentFields = JSON.parse(sub.formData && sub.formData.extraFields || "{}");
			} catch (e) {
				currentFields = {};
			}

			const normalizedFields = {};
			masterKeys.forEach((key) => {
				normalizedFields[key] = key in currentFields ? currentFields[key] : null;
			});

			if (!sub.formData) sub.formData = {};
			sub.formData.extraFields = JSON.stringify(normalizedFields);
		});
	};

	processSubmissionsData = () => {
		const { editedForm } = this.props;
		this.normalizeSubmissions(editedForm.submissions);

		const processedSubmissions = editedForm.submissions.map((a) => {
			if (a.formData['redeemedInventory']) {
				const cleanValue = a.formData['redeemedInventory'].replace(/\n/g, ',');
				a.formData['redeemedInventory'] = cleanValue;
			}

			let extraFields =
				typeof a.formData.extraFields === "string"
					? JSON.parse(a.formData.extraFields)
					: a.formData.extraFields;

			let additionalAttendees = [];
			if (a.formData.additionalAttendees) {
				try {
					additionalAttendees = typeof a.formData.additionalAttendees === "string"
						? JSON.parse(a.formData.additionalAttendees)
						: a.formData.additionalAttendees;
					if (!Array.isArray(additionalAttendees)) {
						additionalAttendees = [];
					}
				} catch (e) {
					additionalAttendees = [];
				}
			}
			a.formData.additionalAttendees = additionalAttendees.map(att => Object.values(att).join(", ")).join("; ");

			let salesforceErrors = a.formData.errors;
			if (salesforceErrors != null) {
				salesforceErrors = salesforceErrors.map(e => {
					return e.error.map(ed => `${ed.message} (${ed.errorCode})`).join(", ");
				}).join("; ");
			}

			return {
				_id: a._id,
				firstName: a.attendee
					? (a.attendee.firstName === "-" ? a.formData.firstName || "-" : a.attendee.firstName)
					: (a.formData.firstName || "-"),
				lastName: a.attendee
					? (a.attendee.lastName === "-" ? a.formData.lastName || "-" : a.attendee.lastName)
					: (a.formData.lastName || "-"),
				email: a.attendee
					? (a.attendee.email === "-" ? a.formData.email || "-" : a.attendee.email)
					: (a.formData.email || "-"),
				submittedAt: a.submittedAt,
				...extraFields,
				...a.formData,
				errors: salesforceErrors,
			};
		});

		const hasErrors = processedSubmissions.some(s => s.errors != null && s.errors !== "");

		this.setState((prevState) => ({
			processedSubmissions,
			filteredSubmissions: processedSubmissions,
			toggledFieldLabels: {
				...prevState.toggledFieldLabels,
				errors: hasErrors,
			},
		}), () => {
			this.applySorting();
		});
	}

	applySorting = () => {
		const { selectedColumnSort, sortDesc, processedSubmissions } = this.state;

		if (!selectedColumnSort || !processedSubmissions) return;

		const sortedData = [...processedSubmissions].sort((a, b) => {
			const columnType = selectedColumnSort.type || typeof a[selectedColumnSort.key];
			if (a[selectedColumnSort.key] && b[selectedColumnSort.key]) {
				switch (columnType) {
					case "string":
						return a[selectedColumnSort.key].toLowerCase().trim()
							.localeCompare(b[selectedColumnSort.key].toLowerCase().trim());
					case "number":
						return a[selectedColumnSort.key] - b[selectedColumnSort.key];
					case "date":
					case "time":
						return new Date(a[selectedColumnSort.key]).getTime() - new Date(b[selectedColumnSort.key]).getTime();
					case "boolean":
						const a1 = a[selectedColumnSort.key] ? "yes" : "no";
						const b1 = b[selectedColumnSort.key] ? "yes" : "no";
						return a1.localeCompare(b1);
					default:
						return a[selectedColumnSort.key].toLowerCase().trim()
							.localeCompare(b[selectedColumnSort.key].toLowerCase().trim());
				}
			} else if (a[selectedColumnSort.key]) {
				return 1;
			}
			return -1;
		});

		const finalSorted = sortDesc ? sortedData.reverse() : sortedData;
		this.applySearchFilter(finalSorted);
	}

	applySearchFilter = (data = null) => {
		const { searchTerm, columns, processedSubmissions } = this.state;
		const dataToFilter = data || processedSubmissions;

		if (!dataToFilter) return;

		if (searchTerm.length === 0) {
			this.setState({ filteredSubmissions: dataToFilter });
			return;
		}

		let filteredData = [...dataToFilter];
		const searchTerms = searchTerm.toLowerCase().split(" ");

		searchTerms.forEach((term) => {
			if (term !== "") {
				filteredData = filteredData.filter((entry) => {
					let found = false;
					Object.keys(columns).forEach((key) => {
						if (entry[key] && entry[key].toString().toLowerCase().includes(term)) {
							found = true;
						}
					});
					return found;
				});
			}
		});

		this.setState({ filteredSubmissions: filteredData });
	}

	sortData = (column, sortAsc, sortDesc) => {
		const { normalizedColumns } = this.state;
		const updatedColumns = normalizedColumns.map((col) => {
			if (col.key === column.key) {
				col.sortAsc = sortAsc;
				col.sortDesc = sortDesc;
			} else {
				col.sortAsc = false;
				col.sortDesc = false;
			}
			return col;
		});

		this.setState({
			normalizedColumns: updatedColumns,
			selectedColumnSort: column,
			sortDesc: sortDesc ? true : false,
		}, () => this.applySorting());
	};

	searchEntries = (searchText) => {
		this.setState({
			searching: searchText !== "",
			searchTerm: searchText || "",
		}, () => this.applySearchFilter());
	};

	handleSearchChange = (e) => {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
		this.setState({ searchTerm: e.target.value }, () => {
			this.searchTimeout = setTimeout(() => {
				this.searchEntries(this.state.searchTerm);
			}, 300);
		});
	};

	clearSearch = () => {
		this.setState({ searchTerm: "", searching: false }, () => this.searchEntries(""));
	};

	handleRowCheck = (entryId) => {
		const checked = { ...this.state.checkedSubmission };
		if (checked[entryId]) {
			delete checked[entryId];
		} else {
			checked[entryId] = true;
		}
		this.setState({ checkedSubmission: checked }, () => this.updateSubmissionEditing());
	};

	handleAllRowsCheck = (entryIds) => {
		let checked = {};
		if (entryIds.length > 0) {
			entryIds.forEach((entryId) => {
				checked[entryId] = true;
			});
		}
		this.setState({ checkedSubmission: checked }, () => this.updateSubmissionEditing());
	};

	updateSubmissionEditing = () => {
		const { checkedSubmission } = this.state;
		const hasCheckedItems = Object.keys(checkedSubmission).length > 0;
		if (hasCheckedItems) {
			this.props.toggleSubmissionEditing(true, checkedSubmission, this.clearCheckedSubmissions);
		} else {
			this.props.toggleSubmissionEditing(false);
		}
	};

	clearCheckedSubmissions = () => {
		this.setState({ checkedSubmission: {} });
	};

	s2ab = (s) => {
		var buf = new ArrayBuffer(s.length);
		var view = new Uint8Array(buf);
		for (var i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
		return buf;
	};

	download = () => {
		const { editedForm } = this.props;
		const { normalizedFieldLabels, filteredSubmissions } = this.state;

		let headers = ["First Name", "Last Name", "Email", "Submitted On"];
		const columns = normalizedFieldLabels.reduce((acc, cur) => {
			acc[cur.key] = cur.label;
			return acc;
		}, {});

		Object.keys(columns).forEach((key) => {
			headers.push(columns[key]);
		});

		const rows = filteredSubmissions.map(sub => {
			let fixedSub = [
				sub.firstName,
				sub.lastName,
				sub.email,
				moment(sub.submittedAt).format("MM/DD/YYYY h:mm a")
			];
			Object.keys(columns).forEach((key) => {
				fixedSub.push(sub[key] || sub[key] === "" ? sub[key] : "");
			});
			return fixedSub;
		});

		var wb = XLSX.utils.book_new();
		wb.Props = {
			Title: editedForm.name + " Submission Report",
			Subject: "Submissions",
			Author: "Simple Events",
			CreatedDate: new Date(),
		};
		wb.SheetNames.push("Data");

		var ws_data2 = [headers, ...rows];
		var ws2 = XLSX.utils.aoa_to_sheet(ws_data2);
		wb.Sheets["Data"] = ws2;

		var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

		saveAs(
			new Blob([this.s2ab(wbout)], { type: "application/octet-stream" }),
			`${editedForm.name} - Submissions.xlsx`
		);
	};

	getCustomFieldColumns = () => {
		const { contactCustomFields } = this.props;
		let customFieldColumns = {};
		let customFieldColumnTypes = {};

		(contactCustomFields || []).forEach((category) => {
			(category.fields || []).forEach((f) => {
				customFieldColumns[f._id] = f.fieldName;
				customFieldColumnTypes[f._id] = f.fieldType;
			});
		});

		return { customFieldColumns, customFieldColumnTypes };
	};

	toggle = (id) => {
		this.setState({
			submissionDetailsModalTrigger: !this.state.submissionDetailsModalTrigger,
			selectedSubmissionId: id || "",
		});
	};

	renderSearchInput = () => {
		const { searchTerm } = this.state;
		return (
			<InputField
				classes="m-0 viewSearch"
				placeholder="Search"
				clickIcon={true}
				required={true}
				value={searchTerm}
				onChange={this.handleSearchChange}
				prefix={
					<i
						className="las la-search"
						style={{ marginRight: 5, transform: "rotate(270deg)" }}
					/>
				}
				inputIcon={
					searchTerm !== "" && (
						<i
							onMouseDown={this.clearSearch}
							className="las la-times-circle"
							style={{ visibility: searchTerm ? "visible" : "hidden" }}
						/>
					)
				}
			/>
		);
	};

	render() {
		const {
			loading,
			normalizedFieldLabels,
			toggledFieldLabels,
			checkedSubmission,
			submissionDetailsModalTrigger,
			selectedSubmissionId,
			filteredSubmissions
		} = this.state;

		const { editedForm, form } = this.props;

		if (loading) {
			return <LoadingWizard text="LOADING SUBMISSIONS" />;
		}

		if (!filteredSubmissions) {
			return <LoadingWizard text="PROCESSING DATA" />;
		}

		const { customFieldColumns, customFieldColumnTypes } = this.getCustomFieldColumns();

		return (
			<div className="sectionContainer">
				<div style={{ height: "100%" }}>
					<div className="actionButtonContainer" style={{ marginBottom: 15 }}>
						<div className="mb-10 mt-10 flex aic">
							{this.renderSearchInput()}
						</div>
						<div className="sectionOptionsContainer">
							<div
								onClick={this.download}
								className="actionButton p-7"
							>
								<div>
									<i className="las la-download" />
								</div>
							</div>
						</div>
					</div>

					<ViewsTable
						title={"Submissions"}
						style={{ margin: 0 }}
						noDescription={true}
						columns={normalizedFieldLabels}
						toggledColumns={toggledFieldLabels}
						data={filteredSubmissions}
						mainColumnActive={false}
						checked={checkedSubmission}
						disableCheck={false}
						onClick={this.toggle}
						checkRow={this.handleRowCheck}
						checkAllRows={this.handleAllRowsCheck}
						sortData={this.sortData}
					/>

					{submissionDetailsModalTrigger && (
						<SubmissionDetailsModal
							isOpen={submissionDetailsModalTrigger}
							submission={editedForm.submissions.find(
								(s) => s._id === selectedSubmissionId
							)}
							columns={customFieldColumns}
							columnTypes={customFieldColumnTypes}
							toggle={this.toggle}
							form={form}
						/>
					)}
				</div>
			</div>
		);
	}
}

export default withRouter(FormSubmissions);

class SubmissionDetailsModal extends React.Component {
	constructor(props) {
		const columns = props.columns;
		const columnTypes = props.columnTypes;
		const toggledColumns = {};
		const normalizedColumns = Object.keys(columns).map((cfc) => {
			toggledColumns[cfc] = true;
			return {
				label: columns[cfc],
				key: cfc,
				value: cfc,
				type: columnTypes[cfc],
				sortAsc: false,
				sortDesc: false,
			};
		});
		super(props);
		this.state = {
			columns,
			columnTypes,
			normalizedColumns,
			toggledColumns,
			submitting: false,
		};
	}

	render() {
		const { isOpen, submission, form } = this.props;
		const { normalizedColumns } = this.state;

		if (!submission) return null;

		const entries =
			typeof submission.formData.extraFields === "string"
				? JSON.parse(submission.formData.extraFields)
				: submission.formData.extraFields;

		return (
			<div>
				<FieldModal
					size="medium"
					isOpen={isOpen}
					bodyDescription={""}
					bodyContent={() => {
						return (
							<div>
								<div className="modalBodySubmission">
									<div className="sectionBlock">
										{form.pages.map((page, pi) => {
											return (
												<div key={pi}>
													<label className="mb-0">Page {pi + 1}</label>
													<h1 className="mb-10">{page.title}</h1>
													{page.sections.map((section, si) => {
														return (
															<div key={si}>
																<h2
																	className="mb-10"
																	dangerouslySetInnerHTML={{ __html: section.title }}
																/>
																{section.fields.map((field, fi) => {
																	let value = "";
																	if (field.objectClass === "baseInput") {
																		if (field.id.includes("-")) {
																			let split = field.id.split("-");
																			const v1 = submission.formData[split[0]];
																			value = v1 ? v1[split[1]] : "";
																		} else {
																			value = submission.formData[field.id];
																		}
																	} else if (field.objectClass === "mappedInput") {
																		value = entries[field.id];
																	} else {
																		value = entries[field._id];
																	}

																	return (
																		<div key={fi}>
																			<div
																				dangerouslySetInnerHTML={{ __html: field.description }}
																			/>
																			<div>
																				{field.label}{" "}
																				{normalizedColumns[field._id] && (
																					<span>- {normalizedColumns[field._id]}</span>
																				)}
																			</div>
																			<InputField value={value} disabled={true}>
																				{entries[field._id]}
																			</InputField>
																			{(field.type === "nestedSections" || field.type === "groupSelection") && (
																				<div>
																					{field.nestedSections.map((nestedSection, nsi) => {
																						return (
																							<div className="nestedSectionBlock" key={nsi}>
																								<h2
																									className="mb-10"
																									dangerouslySetInnerHTML={{ __html: nestedSection.title }}
																								/>
																								{nestedSection.questions.map((question, qi) => {
																									let qValue = "";
																									if (question.objectClass === "baseInput") {
																										if (question.id.includes("-")) {
																											let split = question.id.split("-");
																											const v1 = submission.formData[split[0]];
																											qValue = v1 ? v1[split[1]] : "";
																										} else {
																											qValue = submission.formData[question.id];
																										}
																									} else if (question.objectClass === "mappedInput") {
																										qValue = entries[question.id];
																									} else {
																										qValue = entries[question._id];
																									}
																									return (
																										<div key={qi}>
																											<div
																												dangerouslySetInnerHTML={{ __html: question.description }}
																											/>
																											<div>
																												{question.label}{" "}
																												{normalizedColumns[question._id] && (
																													<span>- {normalizedColumns[question._id]}</span>
																												)}
																											</div>
																											<InputField value={qValue} disabled={true}>
																												{entries[question._id]}
																											</InputField>
																										</div>
																									);
																								})}
																							</div>
																						);
																					})}
																				</div>
																			)}
																		</div>
																	);
																})}
															</div>
														);
													})}
												</div>
											);
										})}
										{(() => {
											const errors = submission.formData.errors;
											if (!errors || !errors.length) return null;
											return (
												<div>
													{errors.map((err, i) => {
														return err.error.map((ed, j) => (
															<div key={`${i}-${j}`}>
																<div>Error</div>
																<InputField value={`${ed.message} (${ed.errorCode})`} disabled={true} />
															</div>
														));
													})}
												</div>
											);
										})()}
									</div>
								</div>
							</div>
						);
					}}
					toggle={this.props.toggle}
					cancelButtonLabel={"Close"}
				/>
			</div>
		);
	}
}
